import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export type PortfolioPreviewItem = {
  src: string;
  kind: "image" | "reel" | "video";
  name: string;
};

type Props = {
  preview: PortfolioPreviewItem | null;
  onClose: () => void;
};

const PortfolioMediaPreview = ({ preview, onClose }: Props) => {
  useEffect(() => {
    if (!preview) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [preview, onClose]);

  if (!preview || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="portfolio-preview-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Media preview"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`portfolio-preview-modal portfolio-preview-modal-${preview.kind}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="portfolio-preview-close"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X size={22} />
        </button>

        <div className="portfolio-preview-stage">
          {preview.kind === "image" ? (
            <img src={preview.src} alt={preview.name} />
          ) : (
            <iframe
              src={preview.src}
              title={preview.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PortfolioMediaPreview;
